import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Paper,
} from "@mui/material";
import { CircularProgress, IconButton } from "@mui/joy";
import { Card, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { DASHBOARD_API_URL } from "../../configs/url";
import apiCall from "../../helpers/api";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { updateUserInfo } from "../../services/user/actions";
import { ContentCopy } from "@mui/icons-material";
import { getSettings } from "../../services/settings/actions";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";

function Dashboard(props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState([]);
  const settings = props?.settings || {};
  const { t } = useTranslation();

  const handleCopy = (text) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          enqueueSnackbar(t("common_copied"), {
            variant: "success",
          });
        })
        .catch(() => {
          enqueueSnackbar(t("common_copy_failed"), {
            variant: "error",
          });
        });
    } else {
      enqueueSnackbar(t("common_copy_failed"), {
        variant: "warning",
      });
    }
  };

  const dashboard = async () => {
    setIsLoading(true);
    try {
      await apiCall({
        method: "POST",
        url: DASHBOARD_API_URL,
      })
        .then((response) => {
          setDashboardData(response.data.data);
        })
        .catch((error) => {
          enqueueSnackbar("Failed to fetch dashboard data", {
            variant: "error",
            autoHideDuration: 3000,
            preventDuplicate: true,
          });
          console.error("Dashboard API error:", error);
        });
    } catch (error) {
      console.error("Dashboard API error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Dashboard | KPG Partner";
    if (!props?.user || !props?.user?.auth_token) {
      window.location.href = "/login";
    }
    props?.getSettings();
    props?.updateUserInfo();
    dashboard();
  }, []);

  // Function to get initials from organization name
  const getInitials = (name) => {
    if (!name) return "OR";
    return name
      .split(" ")
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  // Function to generate avatar color based on name
  const getAvatarColor = (name) => {
    if (!name) return "var(--prime-one)";
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getTransactionStyle = (transaction) => {
    const type =
      transaction.type?.toLowerCase() ||
      (transaction.amount > 0 ? "deposit" : "withdrawal") ||
      "deposit";

    const isDeposit =
      type === "deposit" || type === "credit" || transaction.amount > 0;

    return {
      color: isDeposit ? "#22c55e" : "#ef4444",
      bgColor: isDeposit ? "rgba(34, 197, 94, 0.4)" : "rgba(239, 68, 68, 0.4)",
      icon: isDeposit ? "↙" : "↗",
      prefix: isDeposit ? "+" : "-",
    };
  };

  const RecentReferrals = (drivers) => {
    let recentDrivers =
      drivers?.drivers != undefined || drivers?.drivers != null
        ? Object.values(drivers.drivers)
        : [];

    if (isLoading) {
      return (
        <Paper elevation={0} className="dashboard-paper">
          <Typography variant="h6" className="dashboard-section-title">
            {t("dashboard_recent_referrals")}
          </Typography>
          <List className="dashboard-list">
            {[1, 2, 3, 4, 5].map((item) => (
              <ListItem key={item} className="dashboard-list-item">
                <ListItemAvatar>
                  <Box className="loading-avatar" />
                </ListItemAvatar>
                <Box className="loading-content">
                  <Box className="loading-text-primary" />
                  <Box className="loading-text-secondary" />
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      );
    }

    return (
      <Paper elevation={0} className="dashboard-paper">
        <Typography variant="h6" className="dashboard-section-title">
          {t("dashboard_recent_referrals")}
        </Typography>

        {recentDrivers?.length === 0 ? (
          <Box className="empty-state">
            <Typography variant="body1" className="empty-state-title">
              {t("dashboard_recent_empty_title")}
            </Typography>
            <Typography variant="body2" className="empty-state-subtitle">
              {t("dashboard_recent_empty_subtitle")}
            </Typography>
          </Box>
        ) : (
          <List className="dashboard-list">
            {recentDrivers.map((driver, index) => (
              <ListItem key={index} className="dashboard-list-item">
                <ListItemAvatar>
                  <Avatar
                    src={driver.logo}
                    className="org-avatar"
                    sx={{
                      bgcolor: driver.logo
                        ? "transparent"
                        : getAvatarColor(driver.name),
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  >
                    {!driver.logo && getInitials(driver.name)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body1" className="org-name">
                      {driver.name || "Unknown Driver"}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" className="org-email">
                        {driver.email ||
                          driver.contact_email ||
                          "No email provided"}
                      </Typography>
                      <Typography variant="body2" className="org-email">
                        {driver.phone ||
                          driver.contact_phone ||
                          "No phone provided"}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    );
  };

  const LatestTransactions = () => {
    const recentTransactions =
      dashboardData?.latest_transactions?.slice(0, 5) || [];

    if (isLoading) {
      return (
        <Paper elevation={0} className="dashboard-paper">
          <Typography variant="h6" className="dashboard-section-title">
            {t("dashboard_latest_transactions")}
          </Typography>
          <List className="dashboard-list">
            {[1, 2, 3, 4, 5].map((item) => (
              <ListItem key={item} className="dashboard-list-item">
                <ListItemAvatar>
                  <Box className="loading-avatar" />
                </ListItemAvatar>
                <Box className="loading-content">
                  <Box className="loading-text-primary" />
                  <Box className="loading-text-secondary" />
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      );
    }

    return (
      <Paper elevation={0} className="dashboard-paper">
        <Typography variant="h6" className="dashboard-section-title">
          {t("dashboard_latest_transactions")}
        </Typography>

        {recentTransactions.length === 0 ? (
          <Box className="empty-state">
            <Typography variant="body1" className="empty-state-title">
              {t("dashboard_latest_txn_empty_title")}
            </Typography>
            <Typography variant="body2" className="empty-state-subtitle">
              {t("dashboard_latest_txn_empty_subtitle")}
            </Typography>
          </Box>
        ) : (
          <List className="dashboard-list">
            {recentTransactions.map((transaction, index) => {
              const transactionStyle = getTransactionStyle(transaction);

              return (
                <ListItem key={index} className="dashboard-list-item">
                  <ListItemAvatar>
                    <Avatar
                      className="transaction-avatar"
                      sx={{
                        bgcolor: transactionStyle.bgColor,
                        color: transactionStyle.color,
                        border: `2px solid ${transactionStyle.color}20`,
                      }}
                    >
                      {transactionStyle.icon}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box className="transaction-primary">
                        <Typography
                          variant="body1"
                          className="transaction-description"
                        >
                          {transaction.meta.description ||
                            transaction.reference ||
                            "Transaction"}
                        </Typography>
                        <Typography
                          variant="body1"
                          className="transaction-amount"
                          sx={{ color: transactionStyle.color }}
                        >
                          {settings[5]?.value}{" "}
                          {Math.abs(transaction.amount / 100) || "0.00"}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box className="transaction-secondary">
                        <Typography
                          variant="body2"
                          className="transaction-date"
                        >
                          {moment(transaction.created_at).format("MMM D, YYYY")}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    );
  };

  return (
    <>
      <Box className="dashboard-container">
        <Box className="dashboard-top-section">
          <Card className="welcome-card">
            <Card.Header className="welcome-card-header">
              <Row className="align-items-center">
                <div className="col-12 col-md-6">
                  <h5 className="welcome-title">
                    {t("dashboard_welcome")}, {props.user?.name || "User"}
                  </h5>
                </div>
              </Row>
            </Card.Header>
            <Card.Body className="welcome-card-body">
              <p className="welcome-subtitle">
                {t("dashboard_welcome_subtitle")}
              </p>
            </Card.Body>
          </Card>

          <Card className="referral-info-card">
            <Card.Header className="referral-info-header">
              <Row className="align-items-center">
                <div className="col-12 col-md-6">
                  <h5 className="referral-info-title">
                    {t("dashboard_referral_info")}
                  </h5>
                </div>
              </Row>
            </Card.Header>
            <Card.Body className="referral-info-body">
              <Box className="referral-info-content">
                {/* Ref link */}
                <Box className="referral-link-box">
                  <Typography variant="body2" className="referral-label">
                    {t("referral_link")}:
                  </Typography>
                  <Typography variant="body2" className="referral-value">
                    https://drivers.delivery-kpg.com/driver-agent-form?ref=
                    {props?.user?.referral_code}
                  </Typography>
                  <IconButton
                    onClick={() =>
                      handleCopy(
                        `https://drivers.delivery-kpg.com/driver-agent-form?ref=${props?.user?.referral_code}`
                      )
                    }
                    aria-label="Copy ref link"
                    size="small"
                    className="copy-button"
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Card.Body>
          </Card>

          <Card className="commission-rate-card stat-card">
            <Card.Body className="stat-card-body">
              <div className="stat-icon commission-icon">💵</div>
              <h6 className="stat-label">{t("dashboard_new_drivers")}</h6>
              <h3 className="stat-value number-counter">
                {isLoading ? (
                  <CircularProgress
                    size="sm"
                    sx={{
                      "--CircularProgress-progressColor": "var(--prime-one)",
                    }}
                  />
                ) : (
                  <>{dashboardData?.new_drivers || 0} </>
                )}
              </h3>
              {props?.user?.affiliate_details?.commission_type === "fixed" && (
                <p className="stat-subtitle">
                  {t("dashboard_commission_per_referral")}
                </p>
              )}
            </Card.Body>
          </Card>
        </Box>

        <Box className="dashboard-stats-section">
          <Box className="stat-card-wrapper">
            <Card className="stat-card">
              <Card.Body className="stat-card-body">
                <div className="stat-icon users-icon">👥</div>
                <h6 className="stat-label">{t("dashboard_total_drivers")}</h6>
                <h3 className="stat-value number-counter">
                  {isLoading ? (
                    <CircularProgress
                      size="sm"
                      sx={{
                        "--CircularProgress-progressColor": "var(--prime-one)",
                      }}
                    />
                  ) : (
                    dashboardData?.total_drivers || 0
                  )}
                </h3>
                <p className="stat-subtitle"></p>
              </Card.Body>
            </Card>
          </Box>

          <Box className="stat-card-wrapper">
            <Card className="stat-card">
              <Card.Body className="stat-card-body">
                <div className="stat-icon sessions-icon">⚡</div>
                <h6 className="stat-label">{t("dashboard_active_drivers")}</h6>
                <h3 className="stat-value number-counter">
                  {isLoading ? (
                    <CircularProgress
                      size="sm"
                      sx={{
                        "--CircularProgress-progressColor": "var(--prime-one)",
                      }}
                    />
                  ) : (
                    dashboardData?.hired_count || 0
                  )}
                </h3>
                <p className="stat-subtitle"></p>
              </Card.Body>
            </Card>
          </Box>

          <Box className="stat-card-wrapper">
            <Card className="stat-card">
              <Card.Body className="stat-card-body">
                <div className="stat-icon income-icon">✉️</div>
                <h6 className="stat-label">
                  {t("dashboard_inactive_drivers")}
                </h6>
                <h3 className="stat-value number-counter">
                  {isLoading ? (
                    <CircularProgress
                      size="sm"
                      sx={{
                        "--CircularProgress-progressColor": "var(--prime-one)",
                      }}
                    />
                  ) : (
                    dashboardData?.fired_count || 0
                  )}
                </h3>
                <p className="stat-subtitle"></p>
              </Card.Body>
            </Card>
          </Box>

          <Box className="stat-card-wrapper">
            <Card className="stat-card">
              <Card.Body className="stat-card-body">
                <div className="stat-icon income-icon">💰</div>
                <h6 className="stat-label">{t("dashboard_total_earnings")}</h6>
                <h3 className="stat-value number-counter">
                  {settings[5]?.value}{" "}
                  {isLoading ? (
                    <CircularProgress
                      size="sm"
                      sx={{
                        "--CircularProgress-progressColor": "var(--prime-one)",
                      }}
                    />
                  ) : (
                    dashboardData?.total_earnings || "0.00"
                  )}
                </h3>
                <p className="stat-subtitle"></p>
              </Card.Body>
            </Card>
          </Box>
        </Box>

        <Box className="dashboard-bottom-section">
          <RecentReferrals drivers={dashboardData.latest_drivers} />
          <LatestTransactions />
        </Box>
      </Box>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user.user,
  settings: state.settings.settings,
});

const mapDispatchToProps = {
  updateUserInfo,
  getSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
